import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { View } from '../app/store';

/** Breite eines Elements (ResizeObserver). */
export function useWidth<T extends HTMLElement>(fallback = 800): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.clientWidth || fallback);
    const ro = new ResizeObserver((entries) => {
      const cw = Math.floor(entries[0].contentRect.width);
      if (cw > 0) setW(cw);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fallback]);
  return [ref, w];
}

const VIEWS: View[] = ['portfolio', 'uebersicht', 'detail', 'zukunft', 'vergleich', 'annahmen', 'bericht'];

function readHash(): { view: View; params: URLSearchParams } {
  const h = location.hash.replace(/^#\/?/, '');
  const [path, query] = h.split('?');
  const view = (VIEWS as string[]).includes(path) ? (path as View) : 'uebersicht';
  return { view, params: new URLSearchParams(query ?? '') };
}

function subscribe(cb: () => void) {
  window.addEventListener('hashchange', cb);
  return () => window.removeEventListener('hashchange', cb);
}

export function useRoute(): { view: View; params: URLSearchParams } {
  const hash = useSyncExternalStore(subscribe, () => location.hash, () => '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useStableRoute(hash);
}

function useStableRoute(hash: string) {
  const ref = useRef<{ hash: string; value: ReturnType<typeof readHash> } | null>(null);
  if (!ref.current || ref.current.hash !== hash) ref.current = { hash, value: readHash() };
  return ref.current.value;
}

export function go(view: View, params?: Record<string, string | number>) {
  const q = params ? `?${new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)]))}` : '';
  location.hash = `#/${view}${q}`;
}

export function useMediaQuery(q: string): boolean {
  const [m, setM] = useState(() => (typeof matchMedia !== 'undefined' ? matchMedia(q).matches : false));
  useEffect(() => {
    const mq = matchMedia(q);
    const fn = () => setM(mq.matches);
    mq.addEventListener('change', fn);
    fn();
    return () => mq.removeEventListener('change', fn);
  }, [q]);
  return m;
}

/** Schliesst ein Popover bei Klick ausserhalb oder Escape. */
export function useDismiss(open: boolean, onClose: () => void, ...refs: React.RefObject<HTMLElement | null>[]) {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (refs.some((r) => r.current && r.current.contains(e.target as Node))) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose]);
}
